import React, { useEffect, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { DashboardCard } from "../card";
import PropTypes from "prop-types";
import { Box, CircularProgress } from "@mui/material";
import { DASHBOARD_ITEM_TYPE, DASHBOARD_TREND_GRAPH_LIST } from "../constants";
import { fetchChannelConfig } from "../../../api/channel";
import { CHANNEL_TYPE } from "../../../constants/channel/config";

// create from here because this is HOC
const ResponsiveReactGridLayout = WidthProvider(Responsive);

export const EditDashboardDisplay = React.memo((props) => {
  const { currentVessel, selectedDashboard, setSelectedDashboard } = props;
  const MIN_DIMENSIONS = {
    value: {
      minH: 3,
      minW: 4,
    },
    trend: {
      minH: 9,
      minW: 12,
    },
    combinedTrend: {
      minH: 18,
      minW: 12,
    },
  };
  const defaultProps = {
    className: "layout",
    rowHeight: 30,
    cols: { lg: 36, md: 24, sm: 18, xs: 12, xxs: 6 },
    breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
    isDraggable: true,
    isResizable: true,
  };

  const [channelConfig, setChannelConfig] = useState([]);
  const [isFetchingConfig, setIsFetchingConfig] = useState(false);

  useEffect(() => {
    if (!selectedDashboard) return;

    setIsFetchingConfig(true);
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    const customs = selectedDashboard.items?.filter(item => item.type === DASHBOARD_ITEM_TYPE.CUSTOM);
    if (customs?.length) {
      const ids = [];
      const promises = [];
      customs.forEach((custom) => {
        const config = fetchChannelConfig(currentVessel.id, custom.chartId);
        ids.push(custom.id);
        promises.push(config);
      });
      const resp = await Promise.all(promises);
      setChannelConfig(ids.map((id, idx) => ({
        id,
        ...resp[idx],
      })));
    }
    setIsFetchingConfig(false);
  }

  // for hide resize icon and icon
  const resizeHandle = (resizeHandleAxis, ref) => {
    if (!defaultProps.isResizable) {
      // do not return null here, or it throws error
      return <span className="hidden" />;
    }
    // this is the same as original
    return (
      <span
        ref={ref}
        className={`react-resizable-handle react-resizable-handle-${resizeHandleAxis}`}
      />
    );
  };

  const onLayoutChange = (layout, layouts) => {
    const newItems = selectedDashboard.items?.map((item) => {
      if (item.type === DASHBOARD_ITEM_TYPE.LABEL) return item;

      const target = layout.find((e) => e.i === item.id);
      return {
        ...item,
        position: { i: target.i, x: target.x, y: target.y, h: target.h, w: target.w },
      };
    });
    setSelectedDashboard((current) => ({ ...current, items: newItems }));
  };

  const removeItem = (id) => {
    setSelectedDashboard({
      ...selectedDashboard,
      items: selectedDashboard.items?.filter((e) => e.id !== id),
    });
  };

  if (isFetchingConfig) {
    return <CircularProgress />;
  }

  return (
    <>
      <ResponsiveReactGridLayout
        resizeHandle={resizeHandle}
        onLayoutChange={onLayoutChange}
        {...defaultProps}
        className="layout"
        measureBeforeMount={false}
        draggableCancel=".no-drag"
        style={{
          "& > .reactGridItem > .reactResizableHandle.reactResizableHandleSe::after": {
            opacity: "0 !important",
          },
        }}
      >
        {selectedDashboard?.items?.filter(i => i.type !== DASHBOARD_ITEM_TYPE.LABEL)?.map((el, index) => {
          let dimensionKey = DASHBOARD_TREND_GRAPH_LIST.includes(el.type) || el.type == 'liveTrend' ? 'trend' : el.type;
          if (el.type === DASHBOARD_ITEM_TYPE.CUSTOM) {
            const config = channelConfig.find(c => c.id === el.id)?.config;
            const analogCount = config?.filter(c => c.chType === CHANNEL_TYPE.ANALOG)?.length;
            const isCombined = analogCount > 0 && analogCount !== config?.length;

            if (isCombined) {
              dimensionKey = 'combinedTrend';
            }
          }
          return (
            <div key={el.id} data-grid={{ ...el.position, ...MIN_DIMENSIONS[dimensionKey] }}>
              <Box
                key={el.id}
                className="no-drag"
                onClick={(e) => removeItem(el.id)}
                sx={{
                  position: "absolute",
                  right: "4px",
                  top: "4px",
                  width: "22px",
                  height: "22px",
                  color: "white",
                  cursor: "pointer",
                  zIndex: 999,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "start",
                  background: "#1b202a",
                  borderRadius: "4px",
                }}
              >
                x
              </Box>
              <DashboardCard
                id={el.id}
                type={el.type}
                chNos={el.chNos}
                vessel={currentVessel}
                item={el}
                index={index}
                isEdit={true}
              />
            </div>
          );
        })}
      </ResponsiveReactGridLayout>
    </>
  );
});

EditDashboardDisplay.propTypes = {
  currentVessel: PropTypes.object,
  selectedDashboard: PropTypes.object,
  setSelectedDashboard: PropTypes.func,
};
